import styled from 'styled-components';

import { Column, DataTable, DataTableProps } from '@creditornot/cb-data-table';
import { ApiErrorNotification } from 'common/components/cookbook/ApiErrorNotification';
import { ModalSpinner } from 'common/components/cookbook/Modals/ModalSpinner';
import { Stack } from 'common/components/cookbook/Stack';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import {
  ListOverrideDraftSummary,
  OverrideDraftForecast,
} from 'features/override-management/api-types';

type Props = {
  summaryItems: ListOverrideDraftSummary['items'];
  isLoading: boolean;
  error: Error | null;
};

const Root = styled(Stack)`
  width: 100%;
  flex-direction: column;
  justify-content: center;
`;

const StyledColumn = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
`;

const StyledNumericColumn = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
`;

type Col = Column<any, unknown>;
const StyledDataTable = styled(DataTable as React.FC<DataTableProps<OverrideDraftForecast, Col>>)`
  width: 100%;
  tr td {
    padding: 0;
  }

  tr th {
    padding: 16px;
  }

  tr td:first-child {
    padding-inline-start: 0;
  }

  tr td:last-child {
    padding-inline-end: 0;
  }

  th > div > div {
    margin-inline-end: 0;
  }
`;

export function ReviewSummaryStep({ summaryItems = [], isLoading, error }: Props) {
  const format = useFormatMessage();

  const dates = summaryItems.at(0)?.dateOverrides?.map(({ targetDate }) => targetDate) ?? [];

  return (
    <Root>
      {error && <ApiErrorNotification error={error} />}
      {isLoading ? (
        <ModalSpinner />
      ) : (
        <StyledDataTable
          dataSource={summaryItems}
          getRowKey={(change) => change.productCode}
          emptyMessage={{ description: format('forecasting.no_changes') }}
          columns={[
            {
              id: 'productCode',
              name: format('common.product_code'),
              alignContent: 'left',
              alignName: 'left',
              render: ({ record }) => <StyledColumn>{record.productCode}</StyledColumn>,
            },
            {
              id: 'hierarchyLevel',
              name: format('forecasting.hierarchy_level'),
              alignContent: 'right',
              alignName: 'right',
              render: ({ record }) => (
                <StyledNumericColumn>{record.hierarchyLevel}</StyledNumericColumn>
              ),
            },
            {
              id: 'productFamilyName',
              name: format('common.product_family'),
              alignContent: 'left',
              alignName: 'left',
              render: ({ record }) => <StyledColumn>{record.productFamilyName}</StyledColumn>,
            },
            {
              id: 'venueName',
              name: format('common.venue'),
              alignContent: 'left',
              alignName: 'left',
              render: ({ record }) => <StyledColumn>{record.venueName}</StyledColumn>,
            },
            ...dates.map(
              (targetDate) =>
                ({
                  id: targetDate,
                  name: targetDate,
                  alignName: 'right',
                  alignContent: 'right',
                  render: ({ record }: { record: OverrideDraftForecast }) => {
                    const override = (record.dateOverrides ?? []).find(
                      (override) => override.targetDate === targetDate,
                    );

                    if (!override) {
                      return null;
                    }

                    return <StyledNumericColumn>{override.value ?? '–'}</StyledNumericColumn>;
                  },
                }) as Col,
            ),
            {
              id: 'uom',
              name: format('common.uom'),
              alignContent: 'left',
              alignName: 'left',
              render: ({ record }) => (
                <StyledColumn>
                  {record.measurementUnit
                    ? format(`common.measurement_unit:${record.measurementUnit}`)
                    : '–'}
                </StyledColumn>
              ),
            },
          ]}
        />
      )}
    </Root>
  );
}
